import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaPhoneAlt,
  FaCcVisa,
  FaCcMastercard,
  FaPaypal,
} from "react-icons/fa";
import { FiMail } from "react-icons/fi";
import { MdLocationOn } from "react-icons/md";
import { PackagesContext } from "../context/PackagesContext";
import EnquiryPopup from "./EnquiryPopup";

const Footer = () => {
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  const destinationQueryMap = {
    Dubai: { destination: null, country: "UAE" },
    Thailand: { destination: null, country: "Thailand" },
    "Sri Lanka": { destination: null, country: "Sri Lanka" },
    "Hong Kong": { destination: null, country: "Hong Kong" },
    Maldives: { destination: "Maldives", country: "Maldives" },
    Singapore: { destination: "Singapore", country: "Singapore" },
    Bali: { destination: "Bali", country: "Indonesia" },
    "Andaman & Nicobar": { destination: "Andaman & Nicobar", country: "India" },
    Kerala: { destination: "Kerala", country: "India" },
    Goa: { destination: "Goa", country: "India" },
  };

  const handleDestinationClick = (label) => {
    const redirectDestinations = Object.keys(destinationQueryMap);
    if (redirectDestinations.includes(label)) {
      const { destination, country } = destinationQueryMap[label];

      const queryParams = new URLSearchParams();
      if (destination) queryParams.set("destination", destination);
      if (country) queryParams.set("country", country);

      navigate(`/all_packages?${queryParams.toString()}`);
    } else {
      setShowPopup(true);
    }
  };

  const currentYear = new Date().getFullYear();
  return (
    <footer className="bg-gray-900 text-gray-300 pt-12 px-4 sm:px-6 lg:px-8 border-t-4 border-blue-600">
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
        {/* Company Info */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-2xl font-bold text-white">Tripocation Holiday Pvt. Ltd.</h2>
          <p className="text-sm leading-relaxed">
            Crafting unforgettable journeys. Travel agency specializing in
            personalized travel experiences across the globe.
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <MdLocationOn className="text-blue-500 mt-1" />
              <span>B-3/58 second floor sector-6 Rohini New Delhi-110085</span>
            </div>
            <div className="flex items-center gap-2">
              <FaPhoneAlt className="text-blue-500" />
              <span>+91 92201 98202</span>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <FiMail className="text-blue-500" />
                <a
                  href="mailto:prince@tripocationholiday.com"
                  className="hover:text-blue-400 transition-colors"
                >
                  prince@tripocationholiday.com
                </a>
              </div>
              <div className="flex items-center gap-2 ml-6">
                <a
                  href="mailto:sachin@tripocationholiday.com"
                  className="hover:text-blue-400 transition-colors"
                >
                  sachin@tripocationholiday.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Combined International + Domestic */}
        <div className="flex flex-wrap sm:flex-nowrap gap-6 col-span-1 sm:col-span-2">
          {/* International Destinations */}
          <div className="flex-1 min-w-[140px]">
            <h3 className="text-white font-semibold uppercase tracking-wider text-sm mb-4">
              International
            </h3>
            <ul className="space-y-2 text-sm">
              {["Dubai", "Thailand", "Maldives", "Bali", "Singapore", "Sri Lanka", "Malaysia", "Vietnam"].map((dest) => (
                <li
                  key={dest}
                  className="flex items-center space-x-2 cursor-pointer hover:text-blue-400"
                  onClick={() => handleDestinationClick(dest)}
                >
                  <span className="text-blue-400 text-xs">▹</span>
                  <span>{dest}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Domestic Destinations */}
          <div className="flex-1 min-w-[140px]">
            <h3 className="text-white font-semibold uppercase tracking-wider text-sm mb-4">
              Domestic
            </h3>
            <ul className="space-y-2 text-sm">
              {["Kerala", "Goa", "Himachal", "Kashmir"].map((dest) => (
                <li
                  key={dest}
                  className="flex items-center space-x-2 cursor-pointer hover:text-blue-400"
                  onClick={() => handleDestinationClick(dest)}
                >
                  <span className="text-blue-400 text-xs">▹</span>
                  <span>{dest}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Media & Payment */}
        <div className="space-y-6">
          <div>
            <h3 className="text-white font-semibold uppercase tracking-wider text-sm mb-4">
              Follow Us
            </h3>
            <div className="flex gap-4">
              {[{
                icon: FaFacebook,
                color: "#3b5998",
                link: "https://www.facebook.com/share/1J7KfcZj9b",
              }, {
                icon: FaInstagram,
                color: "#e1306c",
                link: "https://www.instagram.com/tripocation_holiday?igsh=bTdkY2l3bTNkdjM=",
              }].map((social, i) => (
                <a
                  key={i}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-gray-800 transition-colors"
                  style={{ color: social.color }}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 text-gray-400 text-2xl">
            {[FaCcVisa, FaCcMastercard, FaPaypal].map((Icon, index) => (
              <button
                key={index}
                onClick={() => navigate("/pay")}
                className="hover:text-blue-400 transition-colors"
              >
                <Icon />
              </button>
            ))}
            {["UPI", "Net Banking"].map((method) => (
              <button
                key={method}
                onClick={() => navigate("/pay")}
                className="text-xs bg-gray-800 px-2 py-1 rounded hover:text-blue-400 transition-colors"
              >
                {method}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-800 mt-12 pt-6 pb-4 text-center text-xs text-gray-500 mr-24">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span>© {currentYear} Tripocation Holiday Pvt Ltd. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="hover:text-blue-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-blue-400 transition-colors">
              Terms and Conditions
            </Link>
          </div>
        </div>
      </div>
      <EnquiryPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
    </footer>
  );
};

export default Footer;
